import { motion } from "framer-motion";
import { useLanguage } from "@/i18n/LanguageContext";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import ServiceEnquiryForm from "@/components/ServiceEnquiryForm";

const ease = [0.22, 1, 0.36, 1] as const;

const Contact = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        title="Contact Arzani Group — Luxury Concierge"
        description="Speak with our team about private yachts, residences, drive and lifestyle. WhatsApp, email or send us a message."
        path="/contact"
      />
      <Navigation />

      {/* Hero */}
      <section className="pt-40 pb-20 md:pt-52 md:pb-28 bg-warm-black">
        <div className="container-luxury max-w-3xl text-center">
          <motion.span
            className="font-label text-bronze block mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 1.5, ease }}
          >
            Contact
          </motion.span>
          <motion.h1
            className="font-display text-4xl md:text-6xl lg:text-7xl text-primary-foreground leading-[1.05] tracking-wide uppercase"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 1.5, ease }}
          >
            {t("Get In Touch", "Neem Contact Op")}
          </motion.h1>
          <motion.p
            className="font-body text-primary-foreground/50 text-sm md:text-base leading-[1.9] mt-6 max-w-lg mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 1.5, ease }}
          >
            {t(
              "Tell us what you have in mind. We will shape the details around you.",
              "Vertel ons wat u in gedachten heeft. Wij stemmen de details op u af."
            )}
          </motion.p>
        </div>
      </section>

      {/* Form + Contact Info */}
      <section className="section-spacing bg-warm-black">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-[0.72fr_1.28fr] gap-16 lg:gap-24">
            {/* Left: Contact Info */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1.2, ease }}
            >
              <span className="font-label text-bronze block mb-8">
                {t("Direct Contact", "Direct Contact")}
              </span>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-primary-foreground leading-[1.05] mb-10">
                {t("Begin a", "Start een")}<br />{t("conversation.", "gesprek.")}
              </h2>
              <p className="font-body text-primary-foreground/50 text-sm md:text-base max-w-md leading-[1.9] mb-12">
                {t(
                  "For private stays, days at sea and considered dining, our concierge team arranges every detail with discretion.",
                  "Voor privéverblijven, dagen op zee en bijzondere diners regelt ons conciergeteam elk detail met discretie."
                )}
              </p>

              <div className="space-y-5">
                <a
                  href="tel:+31619038233"
                  className="font-label text-primary-foreground/60 hover:text-primary-foreground transition-colors duration-700 block"
                >
                  +31 6 19038233
                </a>
                <a
                  href="https://wa.me/message/2XEKJ5STR2IZJ1"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-label text-primary-foreground/60 hover:text-primary-foreground transition-colors duration-700 block"
                >
                  WhatsApp →
                </a>
                <a
                  href="mailto:info@arzanigroupworldwide.com"
                  className="font-label text-primary-foreground/60 hover:text-primary-foreground transition-colors duration-700 block"
                >
                  info@arzanigroupworldwide.com →
                </a>
              </div>

              {/* Social Links */}
              <div className="mt-12 pt-8 border-t border-primary-foreground/10">
                <span className="font-label text-primary-foreground/30 block mb-5 text-[10px] tracking-[0.2em] uppercase">
                  {t("Follow Us", "Volg Ons")}
                </span>
                <div className="flex gap-6">
                  <a
                    href="https://www.instagram.com/arzani.group/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-label text-primary-foreground/40 hover:text-primary-foreground transition-colors duration-700 text-sm"
                  >
                    Instagram
                  </a>
                  <a
                    href="https://wa.me/message/2XEKJ5STR2IZJ1"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-label text-primary-foreground/40 hover:text-primary-foreground transition-colors duration-700 text-sm"
                  >
                    WhatsApp
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Right: Progressive enquiry */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1.2, delay: 0.15, ease }}
            >
              <ServiceEnquiryForm />
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
